const GENERATED_FILE_URL_PATTERN =
  /\/download\/report\/|\/api\/file\/report\//;

const API_REPORT_URL_PATTERN = /\/api\/file\/report\//;

export function isReportDownloadUrl(url) {
  return (
    /^https?:\/\//i.test(String(url)) &&
    GENERATED_FILE_URL_PATTERN.test(String(url))
  );
}

export function isApiReportUrl(url) {
  return (
    /^https?:\/\//i.test(String(url)) &&
    API_REPORT_URL_PATTERN.test(String(url))
  );
}

export function getFilenameFromUrl(url) {
  try {
    const pathname = new URL(url).pathname;
    const segment = pathname.split("/").pop() || "download";
    return decodeURIComponent(segment);
  } catch {
    return "download";
  }
}

export function parseContentDisposition(header) {
  if (!header) return null;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch) return quotedMatch[1];

  const unquotedMatch = header.match(/filename=([^;]+)/i);
  if (unquotedMatch) return unquotedMatch[1].trim();

  return null;
}

function getExtension(filename) {
  const match = String(filename).match(/\.([^.]+)$/);
  return match ? match[1].toLowerCase() : "";
}

function getClientFileBaseUrl() {
  const fileUrl = process.env.REACT_APP_FILE_URL;
  if (fileUrl) {
    return String(fileUrl).replace(/\/$/, "");
  }

  const serverUrl = process.env.REACT_APP_SERVER_URL;
  if (!serverUrl) return null;

  try {
    const parsed = new URL(serverUrl);
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return null;
  }
}

/**
 * Rewrite report URLs returned by the API when local dev omits the API port
 * (e.g. http://localhost/api/file/report/... -> http://localhost:4001/api/file/report/...).
 */
export function normalizeReportDownloadUrl(url) {
  const normalized = String(url);
  if (!isReportDownloadUrl(normalized)) return normalized;

  const clientBase = getClientFileBaseUrl();
  if (!clientBase) return normalized;

  try {
    const reportUrl = new URL(normalized);
    const clientUrl = new URL(clientBase);

    if (
      reportUrl.hostname === clientUrl.hostname &&
      reportUrl.origin !== clientUrl.origin
    ) {
      return `${clientBase}${reportUrl.pathname}${reportUrl.search}${reportUrl.hash}`;
    }
  } catch {
    return normalized;
  }

  return normalized;
}

function openDirectUrl(url, { mode = "auto" } = {}) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener noreferrer";

  const filename = getFilenameFromUrl(url);
  const extension = getExtension(filename);
  const shouldDownload =
    mode === "download" || (mode === "auto" && extension !== "pdf");

  if (shouldDownload) {
    anchor.download = filename;
  } else {
    anchor.target = "_blank";
  }

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

async function readResponseFile(response, requestUrl) {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("text/html") || contentType.includes("application/json")) {
    const text = await response.text();
    let message = "Server did not return a file";
    try {
      const json = JSON.parse(text);
      message = json.message || json.msg || message;
    } catch {
      if (text && !text.includes("<html")) {
        message = text.slice(0, 200);
      }
    }
    throw new Error(message);
  }

  const buffer = await response.arrayBuffer();
  const dispositionFilename = parseContentDisposition(
    response.headers.get("content-disposition")
  );
  const filename =
    dispositionFilename || getFilenameFromUrl(response.url || requestUrl);
  const extension = getExtension(filename);

  if (extension === "pdf") {
    const header = new TextDecoder().decode(buffer.slice(0, 5));
    if (!header.startsWith("%PDF")) {
      throw new Error("Server did not return a PDF file");
    }
  }

  return {
    buffer,
    contentType: contentType || "application/octet-stream",
    filename,
    extension,
  };
}

function triggerBlobOpen(file, filename, { mode = "auto" } = {}) {
  const blobUrl = URL.createObjectURL(file);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.rel = "noopener noreferrer";

  const extension = getExtension(filename);
  const shouldDownload =
    mode === "download" || (mode === "auto" && extension !== "pdf");

  if (shouldDownload) {
    anchor.download = filename;
  } else {
    anchor.target = "_blank";
  }

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
}

/**
 * Fetch a generated report/export file and open or download it via blob URL.
 * Avoids SPA tab-restore hijacking when /download/ incorrectly serves index.html.
 */
export async function openReportInNewTab(url, { mode = "auto" } = {}) {
  const normalized = normalizeReportDownloadUrl(String(url));
  if (!isReportDownloadUrl(normalized)) {
    throw new Error("Invalid report URL");
  }

  const reportFilename = getFilenameFromUrl(normalized);
  const extension = getExtension(reportFilename);
  const shouldFetch =
    mode === "download" ||
    !isApiReportUrl(normalized) ||
    (mode === "auto" && extension !== "pdf");

  if (isApiReportUrl(normalized) && !shouldFetch) {
    openDirectUrl(normalized, { mode });
    return;
  }

  const response = await fetch(normalized);
  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`);
  }

  const { buffer, contentType, filename } = await readResponseFile(
    response,
    normalized
  );
  const file = new File([buffer], filename, { type: contentType });
  triggerBlobOpen(file, filename, { mode });
}

/**
 * Open any file URL. Generated report URLs use fetch+blob; others open directly.
 */
export async function openFileUrl(url, options = {}) {
  const normalized = String(url);
  if (!normalized) return;

  if (isReportDownloadUrl(normalized)) {
    return openReportInNewTab(normalized, options);
  }

  openDirectUrl(normalized);
}
